import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { TransparentFlatButton } from '@ui/buttons';
import { SpecEditorContext } from './SpecEditorContext';
import { SEVERITY, TIER } from './validate';
import { Card, MicroLabel } from './styles';

// Every problem this editor can see, in one place, with a [Go →] that opens the owning panel,
// switches it out of `{ }` mode and focuses the control.
//
// Two pieces of wording here are not decoration:
//
//   * "Save is never blocked by this list." The server's create/update is lenient; making the
//     client stricter than the server would trap an operator mid-thought.
//   * An `advice` row says out loud that it is house guidance, not a rule the loader enforces.
//     Mixing the two is how an operator learns to ignore both.
//   * A save-tier row is tagged `save`, not `error`, and sorts above the lock-tier ones. Both are
//     errors, but they fail at different doors: a lock-tier row lets the draft be stored and dies at
//     the compile, a save-tier row means the 422 lands on the next Save.
//
// A panel holding `{ }` text that does not parse gets its own row, so the count never reads as a
// clean bill of health for text the editor could not read.
//
// [Go] expands the owning panel, switches it to Form mode and moves focus into it.

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Item = styled.li`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 14px;
  border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
`;

const Sev = styled.span`
  flex: 0 0 auto;
  margin-top: 2px;
  font-size: 10px;
  font-family: monospace;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 1px 7px;
  border-radius: 4px;
  border: 1px solid
    ${({ theme, $sev }) => ($sev === SEVERITY.ERROR ? theme.themeColors.error : theme.themeColors.warning)};
  color: ${({ theme, $sev }) => ($sev === SEVERITY.ERROR ? theme.themeColors.error : theme.themeColors.warning)};
`;

const Path = styled.code`
  display: block;
  font-size: 11.5px;
  color: ${({ theme }) => theme.themeColors.secondaryText};
  margin-bottom: 2px;
  word-break: break-all;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 14px;
`;

const Grow = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ProblemsStrip = () => {
  const { problems, goToPath, unparsedPanels } = React.useContext(SpecEditorContext);

  const errors = problems.filter((p) => p.severity === SEVERITY.ERROR);
  const saveBlockers = errors.filter((p) => p.tier === TIER.SAVE);
  const lockErrors = errors.filter((p) => p.tier !== TIER.SAVE);
  const advice = problems.filter((p) => p.severity === SEVERITY.ADVICE);
  const ordered = saveBlockers.concat(lockErrors, advice);

  return (
    <Card as="section" aria-label="Draft problems">
      <Head>
        <MicroLabel>{`Problems · ${problems.length}`}</MicroLabel>
        <Grow />
        <Body7 isLowContrast>Save is never blocked by this list.</Body7>
      </Head>

      <List>
        {unparsedPanels.length > 0 && (
          <Item>
            <Sev $sev={SEVERITY.ERROR}>unread</Sev>
            <div>
              <Body5>{`${unparsedPanels.length} section${
                unparsedPanels.length === 1 ? '' : 's'
              } contain JSON that does not parse`}</Body5>
              <Container top={2}>
                <Body7 isLowContrast>
                  Those edits are not applied and are not checked. What will be saved is the last version of that
                  section that parsed.
                </Body7>
              </Container>
            </div>
          </Item>
        )}

        {ordered.length === 0 && unparsedPanels.length === 0 ? (
          <Item>
            <Body7 isLowContrast>
              Nothing here contradicts the load-time rules this editor knows about. Those rules are a copy of the
              engine’s, and copies drift — “Check this draft” asks the real compiler.
            </Body7>
          </Item>
        ) : null}

        {ordered.map((problem) => (
          <Item key={`${problem.severity}:${problem.path}:${problem.title}`}>
            <Sev $sev={problem.severity}>{problem.tier === TIER.SAVE ? 'save' : problem.severity}</Sev>
            <Grow>
              <Path>{problem.path}</Path>
              <Body5>{problem.title}</Body5>
              <Container top={2}>
                <Body7 isLowContrast>{problem.explain}</Body7>
              </Container>
            </Grow>
            <TransparentFlatButton onClick={() => goToPath(problem.path, problem.panel)}>Go</TransparentFlatButton>
          </Item>
        ))}
      </List>
    </Card>
  );
};
